import { http } from '../lib/api/http';
import { listCompanyJoinRequests } from './companyJoinRequests';
import { uploadFileWithPresign } from './uploadsApi';
import {
  rememberCompanyPortalSlug,
  clearCompanyPortalSlug,
} from '../constants/companyRoutes';

export const defaultCompanyValues = {
  id: '',
  name: '',
  slug: '',
  slogan: '',
  phoneNumber: '',
  facebookUrl: '',
  linkedinUrl: '',
  websiteUrl: '',
  description: '',
  street: '',
  streetNumber: '',
  locationDisplay: '',
  locationUnlocode: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  logoFile: null,
  logoUrl: '',
  logoPreview: '',
  heroFile: null,
  heroUrl: '',
  heroPreview: '',
  heroOffset: 50,
  rfc: '',
  status: 'draft',
  publicationLimit: 0,
  userLimit: 0,
  canPublish: false,
  isVerified: false,
  ownerId: '',
  ownerIds: [],
  ownerNames: [],
  ownerName: '',
  category: '',
  categories: [],
  companyType: '',
  membershipPlan: 'free',
  ppcCredits: 0,
  leadCredits: 0,
  membershipRenewsAt: '',
  views: 0,
};

const LOGO_UPLOAD_KIND = 'avatar';
const HERO_UPLOAD_KIND = 'hero';
const PUBLIC_COMPANY_VISITS_ENDPOINT = '/public/companies';

const isBrowserFile = value => typeof File !== 'undefined' && value instanceof File;

const getAssetUrl = (record, key) => {
	if (!record) return '';
	const camel = `${key}Url`;
	const snake = `${key}_url`;
	if (record[snake]) return record[snake];
	if (record[camel]) return record[camel];
	return '';
};


const normalizeId = value => {
  if (!value) return '';
  if (Array.isArray(value)) {
    return value[0] || '';
  }
  if (typeof value === 'object' && value !== null) {
    return value.id || '';
  }
  return String(value);
};

const ensureArray = value => {
  if (Array.isArray(value)) {
    return value;
  }
  if (value === null || value === undefined || value === '') {
    return [];
  }
  return [value];
};

const extractOwnerIds = value =>
  ensureArray(value)
    .map(item => normalizeId(item))
    .filter(Boolean);

const normalizeCategoryList = value => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(item => String(item).trim()).filter(Boolean);
  }
  const raw = String(value).trim();
  if (!raw) return [];
  if ((raw.startsWith('[') && raw.endsWith(']')) || raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map(item => String(item).trim()).filter(Boolean);
      }
    } catch {
      // ignore parse errors
    }
  }
  return raw
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
};

const parseCompanyViews = record => {
  if (!record || typeof record !== 'object') {
    return 0;
  }
  const candidates = [
    record.views,
    record.viewCount,
    record.view_count,
    record.profileViews,
    record.profile_views,
    record.totalViews,
  ];
  for (const value of candidates) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return Math.max(0, Math.round(numeric));
    }
  }
  return 0;
};

const mapCompanyProfileViewRecord = record => {
  if (!record) {
    return {
      id: '',
      companyId: '',
      viewerId: '',
      viewerLabel: '',
      viewerUsername: '',
      viewerEmail: '',
      isGuest: true,
      created: '',
    };
  }

  const expandedViewer = Array.isArray(record.expand?.viewer)
    ? record.expand.viewer[0]
    : record.expand?.viewer;

  const rawViewer = record.viewer_id || record.viewer || expandedViewer;
  const viewerId = normalizeId(rawViewer);

  const viewerLabel =
    record.viewer_label ||
    record.viewerLabel ||
    expandedViewer?.name ||
    expandedViewer?.username ||
    expandedViewer?.email ||
    (viewerId ? 'Miembro de Flotte' : 'Invitado');

  const viewerUsername =
    record.viewer_username ||
    expandedViewer?.username ||
    '';
  const viewerEmail = record.viewer_email || expandedViewer?.email || '';

  return {
    id: record.id || '',
    companyId: normalizeId(record.company_id || record.company),
    viewerId,
    viewerLabel,
    viewerUsername,
    viewerEmail,
    isGuest: !viewerId,
    created: record.created_at || record.created || '',
  };
};

const mapCompanyRecordToValues = record => {
  if (!record) return { ...defaultCompanyValues };
  const normalizedCategories = normalizeCategoryList(
    record.category || record.categories || record.category_list
  );
  const ownerSource = record.owner ?? record.owner_id ?? record.ownerId;
  const ownerIdsList = extractOwnerIds(ownerSource);
  const ownerNamesList = ensureArray(record?.expand?.owner)
    .map(entry => entry?.name || entry?.email || '')
    .filter(Boolean);
  return {
    ...defaultCompanyValues,
    id: record.id,
    name: record.name || '',
    slogan: record.slogan || '',
    phoneNumber: record.phoneNumber || record.phone_number || record.telefono || '',
    facebookUrl: record.facebookUrl || record.facebook_url || '',
    linkedinUrl: record.linkedinUrl || record.linkedin_url || '',
    websiteUrl: record.websiteUrl || record.website_url || record.website || '',
    description: record.description || record.about || '',
    slug: record.slug || '',
    street: record.street || record.direccion1 || '',
    streetNumber: record.streetNumber || record.direccion2 || '',
    locationUnlocode:
      record.locationUnlocode ||
      record.location_unlocode ||
      record.locationLocode ||
      record.location_locode ||
      '',
    locationDisplay:
      record.locationDisplay ||
      record.location_display ||
      (() => {
        const parts = [record.city, record.state, record.country].filter(Boolean);
        return parts.join(', ');
      })(),
    city: record.city || '',
    state: record.state || '',
    country: record.country || '',
    postalCode: record.postalCode || record.postal_code || record.cp || '',
    rfc: record.rfc || record.taxId || '',
    logoUrl: getAssetUrl(record, 'logo'),
    heroUrl: getAssetUrl(record, 'hero'),
    heroOffset:
      typeof record.heroOffset === 'number' && Number.isFinite(record.heroOffset)
        ? record.heroOffset
        : 50,
    status: record.status || 'draft',
    publicationLimit: Number.isFinite(Number(record.publicationLimit))
      ? Number(record.publicationLimit)
      : 0,
    userLimit: Number.isFinite(Number(record.userLimit)) ? Number(record.userLimit) : 0,
    canPublish: Boolean(record.canPublish),
    isVerified: Boolean(record.isVerified),
    ownerIds: ownerIdsList,
    ownerNames: ownerNamesList,
    ownerId: ownerIdsList[0] || '',
    ownerName: ownerNamesList[0] || '',
    category:
      typeof record.category === 'string' && record.category
        ? record.category
        : normalizedCategories[0] || '',
    categories: normalizedCategories,
    companyType:
      (typeof record.companyType === 'string' && record.companyType) ||
      (typeof record.company_type === 'string' && record.company_type) ||
      (typeof record.type === 'string' && record.type) ||
      '',
    membershipPlan:
      record.membershipPlan || record.membership_plan || record.subscriptionPlan || 'free',
    ppcCredits: Number.isFinite(Number(record.ppcCredits))
      ? Number(record.ppcCredits)
      : Number(record.includedPpcCredits) || 0,
    leadCredits: Number.isFinite(Number(record.leadCredits))
      ? Number(record.leadCredits)
      : Number(record.includedLeadCredits) || 0,
    membershipRenewsAt: record.membershipRenewsAt || record.subscriptionRenewsAt || '',
    views: parseCompanyViews(record),
  };
};

const DEFAULT_COMPANY_SUGGESTION_LIMIT = 8;


const fetchCompanies = async (params = {}) => {
  const response = await http.get('/companies', { params });
  return Array.isArray(response.data) ? response.data : [];
};

const stripDiacritics = value => {
  const text = String(value ?? '');
  if (typeof text.normalize === 'function') {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  return text;
};

const buildSlugSearchTerm = term => stripDiacritics(term)
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

export async function searchCompaniesByName(query, { limit = DEFAULT_COMPANY_SUGGESTION_LIMIT } = {}) {
  const term = String(query || '').trim();
  if (!term) {
    return [];
  }

  const needle = term.toLowerCase();
  const normalizedSlugTerm = buildSlugSearchTerm(term);

  try {
    const companies = await fetchCompanies({ status: 'published' });
    return companies
      .filter(record => {
        const candidates = [
          record.name,
          record.slug,
          record.displayName,
          record.legalName,
          record.tradeName,
        ]
          .map(value => String(value || '').toLowerCase())
          .filter(Boolean);

        return candidates.some(value => value.includes(needle) || value.includes(normalizedSlugTerm));
      })
      .slice(0, limit)
      .map(record => {
        const ownerIds = extractOwnerIds(record.owner_id || record.owner);
        return {
          id: record.id,
          name: record.name || '',
          slug: record.slug || '',
          ownerIds,
          ownerId: ownerIds[0] || '',
          ownerEmail: '',
          ownerName: '',
          location: [record.city, record.state, record.country]
            .map(value => (value || '').trim())
            .filter(Boolean)
            .join(', '),
        };
      });
  } catch (error) {
    console.error('No se pudieron buscar empresas.', error);
    return [];
  }
}

const USER_ROLE_LABELS = {
  admin: 'Administrador',
  provider: 'Proveedor',
  client: 'Cliente',
};

const formatMembershipTenure = createdAt => {
  if (!createdAt) return '';
  try {
    const createdDate = new Date(createdAt);
    if (Number.isNaN(createdDate.getTime())) return '';
    const now = new Date();
    const diffMs = Math.max(0, now.getTime() - createdDate.getTime());
    const yearMs = 1000 * 60 * 60 * 24 * 365;
    const monthMs = 1000 * 60 * 60 * 24 * 30;
    const years = Math.floor(diffMs / yearMs);
    if (years >= 1) {
      return `${years} ${years === 1 ? 'ano' : 'anos'} en Flotte`;
    }
    const months = Math.max(1, Math.floor(diffMs / monthMs));
    return `${months} ${months === 1 ? 'mes' : 'meses'} en Flotte`;
  } catch {
    return '';
  }
};

const mapCompanyPersonRecord = record => {
  if (!record) {
    return null;
  }

  const expandCompany = record?.expand?.company;
  const locationParts = [];
  if (record?.city) locationParts.push(record.city);
  if (record?.state) locationParts.push(record.state);
  if (!locationParts.length && record?.country) {
    locationParts.push(record.country);
  }
  if (Array.isArray(expandCompany)) {
    const firstCompany = expandCompany[0];
    if (firstCompany?.city) locationParts.push(firstCompany.city);
    if (firstCompany?.state) locationParts.push(firstCompany.state);
    if (!locationParts.length && firstCompany?.country) {
      locationParts.push(firstCompany.country);
    }
  } else if (expandCompany) {
    if (expandCompany?.city) locationParts.push(expandCompany.city);
    if (expandCompany?.state) locationParts.push(expandCompany.state);
    if (!locationParts.length && expandCompany?.country) {
      locationParts.push(expandCompany.country);
    }
  }

  const location = locationParts.join(', ');
  const roleLabel =
    record?.role_label ||
    record?.headline ||
    record?.tagline ||
    record?.position ||
    record?.title ||
    USER_ROLE_LABELS[record?.role] ||
    record?.role ||
    'Miembro';

	const avatarUrl = record?.avatar_url || record?.avatarUrl || '';

  const aboutRaw =
    typeof record?.aboutMe === 'string'
      ? record.aboutMe
      : typeof record?.about_me === 'string'
      ? record.about_me
      : typeof record?.bio === 'string'
      ? record.bio
      : typeof record?.about === 'string'
      ? record.about
      : '';

  const createdAt = record?.created_at || record?.createdAt || record?.created || '';

  return {
    id: record?.id || '',
    username: record?.username || '',
    name:
      record?.display_name ||
      record?.name ||
      record?.username ||
      record?.email ||
      'Usuario sin nombre',
    role: roleLabel,
    tenure: formatMembershipTenure(createdAt),
    location,
    bio: aboutRaw.trim(),
    avatarUrl,
  };
};

const mapUserRecord = user => ({
  id: user?.id || '',
  displayName:
    user?.display_name ||
    user?.displayName ||
    user?.name ||
    user?.email ||
    'Usuario sin nombre',
  email: user?.email || '',
  role: user?.role || 'user',
  avatarUrl:
    user?.avatar_url ||
    user?.avatarUrl ||
    getAssetUrl(user, 'avatar', { thumb: '120x120' }),
});

export async function fetchCompanyContext(currentUser) {
	if (!currentUser) {
		clearCompanyPortalSlug();
		return {
			companyId: '',
			values: { ...defaultCompanyValues },
			users: [],
			canEditCompany: false,
			canManageUsers: false,
		};
	}

	const userId = currentUser.id;
	const directCompanyId = normalizeId(currentUser.company);
	const slugCandidate = rememberCompanyPortalSlug(currentUser.company, currentUser);
	const identifiersToTry = [directCompanyId, slugCandidate].filter(Boolean);

	let companyRecord = null;
	for (const identifier of identifiersToTry) {
		companyRecord = await fetchCompanyFromApi(identifier);
		if (companyRecord) break;
	}

	if (!companyRecord) {
		clearCompanyPortalSlug();
		return {
			companyId: '',
			values: { ...defaultCompanyValues },
			users: [],
			canEditCompany: false,
			canManageUsers: false,
		};
	}

  const companyId = companyRecord.id || directCompanyId || '';
  rememberCompanyPortalSlug(companyRecord, currentUser);
  const ownerIds = extractOwnerIds(companyRecord.owner ?? companyRecord.owner_id);
  const isOwner = ownerIds.includes(userId);
  const isSuperUser = ['superuser', 'admin', 'company_admin'].includes(currentUser.role);

	let users = await fetchCompanyUsersFromApi(companyId);

  const expandedOwners = ensureArray(companyRecord.expand?.owner);
  ownerIds.forEach(ownerId => {
    if (!ownerId || users.some(user => user.id === ownerId)) {
      return;
    }
    const matched = expandedOwners.find(entry => normalizeId(entry) === ownerId);
    if (matched) {
      users = [
        mapUserRecord({
          ...matched,
          role: 'Owner',
        }),
        ...users,
      ];
    }
  });

  let joinRequests = [];
  if (isOwner || isSuperUser) {
    try {
      joinRequests = await listCompanyJoinRequests(companyId, { includeResolved: true });
    } catch (error) {
      if (![401, 403, 404].includes(error?.status)) {
        console.warn('No se pudieron obtener las solicitudes de unión.', error);
      }
    }
  }

	return {
		companyId,
		values: mapCompanyRecordToValues(companyRecord),
		users,
		canEditCompany: isOwner || isSuperUser,
		canManageUsers: isOwner || isSuperUser,
		userRequests: joinRequests,
	};
}

const cleanPayload = payload =>
  Object.entries(payload).reduce((acc, [key, value]) => {
    if (typeof value === 'undefined') {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});

const resolveCategoriesPayload = (primary, fallback) => {
  if (Array.isArray(primary)) {
    return primary.map(item => String(item).trim()).filter(Boolean);
  }
  if (Array.isArray(fallback)) {
    return fallback.map(item => String(item).trim()).filter(Boolean);
  }
  if (typeof primary === 'string' && primary.trim()) {
    return normalizeCategoryList(primary);
  }
  if (typeof fallback === 'string' && fallback.trim()) {
    return normalizeCategoryList(fallback);
  }
  return [];
};

const normalizeOffset = value => {
  const offset = Number(value);
  if (!Number.isFinite(offset)) {
    return undefined;
  }
  return Math.min(100, Math.max(0, Math.round(offset)));
};

const normalizeLimit = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return undefined;
  }
  return Math.max(0, Math.round(numeric));
};

const normalizeStringOrEmpty = value => {
  if (value === null || typeof value === 'undefined') {
    return '';
  }
  return String(value ?? '').trim();
};

async function maybeAttachAsset(payload, fieldName, fileValue, currentUrl, { kind, includeExistingValue = false } = {}) {
  if (isBrowserFile(fileValue)) {
    const presign = await uploadFileWithPresign({ kind, file: fileValue });
    payload[fieldName] = presign?.public_url || '';
    return;
  }
  if (fileValue === null) {
    payload[fieldName] = '';
    return;
  }
  if (includeExistingValue && currentUrl) {
    payload[fieldName] = currentUrl;
  }
}

const buildCompanyPayload = async (
  values = {},
  { includeExistingAssets = false, applyDefaults = false } = {}
) => {
  const payload = {
    name: normalizeStringOrEmpty(values.name),
    slug: normalizeStringOrEmpty(values.slug),
    slogan: normalizeStringOrEmpty(values.slogan),
    description: normalizeStringOrEmpty(values.description),
    phone_number: normalizeStringOrEmpty(values.phoneNumber),
    facebook_url: normalizeStringOrEmpty(values.facebookUrl),
    linkedin_url: normalizeStringOrEmpty(values.linkedinUrl),
    website_url: normalizeStringOrEmpty(values.websiteUrl),
    street: normalizeStringOrEmpty(values.street),
    street_number: normalizeStringOrEmpty(values.streetNumber),
    location_display: normalizeStringOrEmpty(values.locationDisplay),
    location_unlocode: normalizeStringOrEmpty(values.locationUnlocode),
    city: normalizeStringOrEmpty(values.city),
    state: normalizeStringOrEmpty(values.state),
    country: normalizeStringOrEmpty(values.country),
    postal_code: normalizeStringOrEmpty(values.postalCode),
    rfc: normalizeStringOrEmpty(values.rfc),
    status: undefined,
    can_publish: Boolean(values.canPublish),
    is_verified: Boolean(values.isVerified),
  };

  const statusValue = normalizeStringOrEmpty(values.status);
  if (statusValue) {
    payload.status = statusValue;
  } else if (applyDefaults) {
    payload.status = 'draft';
  }

  const offset = normalizeOffset(values.heroOffset);
  if (typeof offset !== 'undefined') {
    payload.hero_offset = offset;
  }

  const publicationLimit = normalizeLimit(values.publicationLimit);
  if (typeof publicationLimit !== 'undefined') {
    payload.publication_limit = publicationLimit;
  }

  const userLimit = normalizeLimit(values.userLimit);
  if (typeof userLimit !== 'undefined') {
    payload.user_limit = userLimit;
  }

  if (values.ownerId) {
    payload.owner_id = values.ownerId;
  }

  const membershipPlanValue = normalizeStringOrEmpty(values.membershipPlan);
  if (membershipPlanValue) {
    payload.membership_plan = membershipPlanValue.toLowerCase();
  } else if (applyDefaults) {
    payload.membership_plan = 'free';
  }

  if (values.membershipRenewsAt) {
    payload.membership_renews_at = values.membershipRenewsAt;
  }

  const categories = resolveCategoriesPayload(values.categories, values.category);
  if (categories.length) {
    payload.categories = categories;
  }

  await maybeAttachAsset(payload, 'logo_url', values.logoFile, values.logoUrl, {
    kind: LOGO_UPLOAD_KIND,
    includeExistingValue: includeExistingAssets,
  });
  await maybeAttachAsset(payload, 'hero_url', values.heroFile, values.heroUrl, {
    kind: HERO_UPLOAD_KIND,
    includeExistingValue: includeExistingAssets,
  });

  if (includeExistingAssets && !payload.logo_url && values.logoUrl) {
    payload.logo_url = values.logoUrl;
  }
  if (includeExistingAssets && !payload.hero_url && values.heroUrl) {
    payload.hero_url = values.heroUrl;
  }

  return cleanPayload(payload);
};

const fetchCompanyFromApi = async identifier => {
  if (!identifier) {
    return null;
  }
  try {
    const response = await http.get(`/companies/${encodeURIComponent(identifier)}`);
    return response.data || null;
  } catch (error) {
    if (![401, 403, 404].includes(error?.status)) {
      console.warn('No se pudo obtener la empresa desde el backend.', error);
    }
    return null;
  }
};

const fetchCompanyUsersFromApi = async companyId => {
  if (!companyId) {
    return [];
  }
  try {
    const response = await http.get('/users', { params: { company_id: companyId } });
    const items = Array.isArray(response.data) ? response.data : [];
    return items.map(mapUserRecord);
  } catch (error) {
    if (![401, 403, 404].includes(error?.status)) {
      console.warn('No se pudieron obtener los usuarios desde el backend.', error);
    }
    return [];
  }
};

export async function updateCompanyProfile(companyId, values) {
  if (!companyId) {
    throw new Error('No se encontro la empresa.');
  }

  const payload = await buildCompanyPayload(values);
  const response = await http.patch(`/companies/${encodeURIComponent(companyId)}`, payload);
  const record = response.data || {};
  rememberCompanyPortalSlug(record);
  return mapCompanyRecordToValues(record);
}

export async function patchCompanyProfile(
  companyId,
  { phoneNumber, description, city, state, country, heroFile, logoFile } = {},
  { slug } = {}
) {
  const identifier = companyId || slug;
  if (!identifier) {
    throw new Error('No se encontro la empresa.');
  }

  const payload = {};
  const applyString = (key, value) => {
    if (typeof value === 'undefined') {
      return;
    }
    payload[key] = normalizeStringOrEmpty(value);
  };

  applyString('phone_number', phoneNumber);
  applyString('description', description);
  applyString('city', city);
  applyString('state', state);
  applyString('country', country);

  await maybeAttachAsset(payload, 'hero_url', heroFile, undefined, { kind: HERO_UPLOAD_KIND });
  await maybeAttachAsset(payload, 'logo_url', logoFile, undefined, { kind: LOGO_UPLOAD_KIND });

  if (!Object.keys(payload).length) {
    return null;
  }

  const response = await http.patch(`/companies/${encodeURIComponent(identifier)}`, payload);
  const record = response.data || {};
  rememberCompanyPortalSlug(record);
  return record;
}

export async function createCompanyProfile(values, { ownerId } = {}) {
  const payload = await buildCompanyPayload(values, {
    includeExistingAssets: true,
    applyDefaults: true,
  });
  if (ownerId) {
    payload.owner_id = ownerId;
  }
  const response = await http.post('/companies', payload);
  const record = response.data || {};
  rememberCompanyPortalSlug(record);
  return mapCompanyRecordToValues(record);
}

export async function setCompanyMembershipPlan(companyId, nextPlan = 'free', options = {}) {
  if (!companyId) {
    throw new Error('No se encontro la empresa.');
  }
  const payload = {
    membership_plan: String(nextPlan || 'free').toLowerCase(),
  };
  if (Object.prototype.hasOwnProperty.call(options, 'membershipRenewsAt')) {
    payload.membership_renews_at = options.membershipRenewsAt || '';
  }
  if (Object.prototype.hasOwnProperty.call(options, 'ppcCredits')) {
    payload.credits = Number(options.ppcCredits) || 0;
  }
  if (Object.prototype.hasOwnProperty.call(options, 'leadCredits')) {
    payload.lead_credits = options.leadCredits;
  }
  if (Object.prototype.hasOwnProperty.call(options, 'canPublish')) {
    payload.can_publish = Boolean(options.canPublish);
  }
  if (Object.prototype.hasOwnProperty.call(options, 'publicationLimit')) {
    payload.publication_limit = options.publicationLimit;
  }
  if (Object.prototype.hasOwnProperty.call(options, 'userLimit')) {
    payload.user_limit = options.userLimit;
  }

  const response = await http.patch(`/companies/${encodeURIComponent(companyId)}`, payload);
  const record = response.data || {};
  rememberCompanyPortalSlug(record);
  return mapCompanyRecordToValues(record);
}

const generateTempPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let pwd = '';
  for (let i = 0; i < 10; i += 1) {
    pwd += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return pwd;
};

export async function inviteCompanyUser(companyId, { email, name }) {
  if (!companyId) {
    throw new Error('No se encontro la empresa.');
  }
  if (!email) {
    throw new Error('El correo es obligatorio.');
  }

  const password = generateTempPassword();
  const payload = {
    email: email.trim().toLowerCase(),
    password,
    display_name: name?.trim() || '',
    role: 'company_user',
    company_id: companyId,
  };

  const response = await http.post('/admin/users', payload);
  return { user: mapUserRecord(response.data || {}), password };
}

export async function fetchCompanyPeople(companyId) {
  if (!companyId) {
    return [];
  }

  const hydratePerson = person => ({
    ...person,
    location: person.location || '',
    bio: person.bio || '',
  });

	try {
		const response = await http.get('/users', { params: { company_id: companyId } });
		const items = Array.isArray(response.data) ? response.data : [];
		return items
			.map(mapCompanyPersonRecord)
			.filter(person => person && person.name)
			.map(hydratePerson);
	} catch (error) {
		if (![401, 403, 404].includes(error?.status)) {
			console.warn('No se pudieron obtener los miembros desde el backend.', error);
		}
		return [];
	}
}

export async function fetchCompanyPublicProfile(slug) {
	const identifier = String(slug || '').trim();
	if (!identifier) {
		throw new Error('Slug no proporcionado.');
	}

	let record = null;
	try {
		const response = await http.get(`/companies/${encodeURIComponent(identifier)}`);
		record = response.data || null;
	} catch (error) {
		if (error?.status === 404) {
			throw new Error('No se encontro la empresa solicitada.');
		}
		throw error;
	}

	if (!record) {
		throw new Error('No se encontro la empresa solicitada.');
	}

	rememberCompanyPortalSlug(record);
	const locationParts = [record.city, record.state, record.country]
		.map(value => (value || '').trim())
		.filter(Boolean);
	const ownerIds = record.owner_id ? [record.owner_id] : [];

	return {
		id: record.id,
		slug: record.slug || identifier,
		nombre: record.name || '',
		banner: getAssetUrl(record, 'hero'),
		logo: getAssetUrl(record, 'logo'),
		tagline: record.slogan || '',
		industry: record.category || '',
		baseOperacion: locationParts.join(', '),
		telefono: record.phone_number || record.phoneNumber || '',
		contacto: '',
		ownerIds,
		city: record.city || '',
		state: record.state || '',
		country: record.country || '',
		websiteUrl: record.website_url || record.websiteUrl || '',
		facebookUrl: record.facebook_url || record.facebookUrl || '',
		linkedinUrl: record.linkedin_url || record.linkedinUrl || '',
		membershipPlan: record.membership_plan || record.membershipPlan || 'free',
		verificado: Boolean(record.is_verified || record.isVerified),
		description: record.description || '',
		ownerPerson: null,
		views: parseCompanyViews(record),
		memberSince: record.created_at || record.created || '',
	};
}

export async function registerCompanyVisit({
	companyId,
	slug,
	viewerLabel = '',
} = {}) {
	const identifier = String(companyId || slug || '').trim();
	if (!identifier) {
		return null;
	}

	const payload = {};
	if (viewerLabel) {
		payload.viewer_label = viewerLabel;
	}

	try {
		const response = await http.post(
			`${PUBLIC_COMPANY_VISITS_ENDPOINT}/${encodeURIComponent(identifier)}/visits`,
			payload
		);
		const views = Number(response.data?.views);
		return Number.isFinite(views) ? Math.max(0, Math.round(views)) : null;
	} catch (error) {
		console.warn('No se pudo registrar la visita del perfil de empresa.', error);
		return null;
	}
}

export async function listCompanyProfileViews(companyId, { limit = 20 } = {}) {
  if (!companyId) {
    return { items: [], total: 0 };
  }

  try {
    const params = { company_id: companyId };
    if (limit) {
      params.limit = limit;
    }
    const response = await http.get('/company-profile-views', { params });
    const items = Array.isArray(response.data) ? response.data.map(mapCompanyProfileViewRecord) : [];
    return {
      items,
      total: items.length,
    };
  } catch (error) {
    console.warn('No se pudieron obtener las visitas recientes del perfil.', error);
    return { items: [], total: 0 };
  }
}

export async function getCompanyById(id) {
  if (!id) {
    throw new Error('El identificador de la empresa es requerido.');
  }

  const response = await http.get(`/companies/${encodeURIComponent(id)}`);
  const record = response.data || {};

  return {
    id: record.id,
    name: record.name || '',
    slug: record.slug || '',
    logoUrl: getAssetUrl(record, 'logo'),
    bannerUrl: getAssetUrl(record, 'hero'),
  };
}

const buildCompanyLocation = record => {
  const parts = [record.city, record.state, record.country]
    .map(value => (value || '').trim())
    .filter(Boolean);
  return parts.join(', ');
};

export async function listPublishedCompanies({ limit = 120, page = 1 } = {}) {
  try {
    const companies = await fetchCompanies({ status: 'published' });
    const normalized = Array.isArray(companies) ? companies : [];
    const start = Math.max(0, (page - 1) * limit);
    const slice = normalized.slice(start, start + limit);

    return slice.map(record => {
      const membershipPlan = String(
        record.membershipPlan ||
          record.membership_plan ||
          record.subscriptionPlan ||
          record.plan ||
          ''
      )
        .trim()
        .toLowerCase();

      const ratingCandidates = [
        record.rating,
        record.averageRating,
        record.reviewScore,
        record.score,
      ];
      const rating =
        ratingCandidates
          .map(value => {
            const numeric = Number(value);
            return Number.isFinite(numeric) ? numeric : null;
          })
          .find(value => value !== null) || null;

      const responseTimeValue =
        (typeof record.sla === 'string' && record.sla.trim()) ||
        (typeof record.responseTime === 'string' && record.responseTime.trim()) ||
        '';

      const coverageValue =
        (typeof record.coverage === 'string' && record.coverage.trim()) ||
        buildCompanyLocation(record) ||
        '';

      const taglineValue =
        (typeof record.slogan === 'string' && record.slogan.trim()) ||
        (typeof record.tagline === 'string' && record.tagline.trim()) ||
        (typeof record.description === 'string' && record.description.trim()) ||
        '';

      const locationUnlocode =
        (typeof record.locationUnlocode === 'string' && record.locationUnlocode.trim()) ||
        (typeof record.location_unlocode === 'string' && record.location_unlocode.trim()) ||
        (typeof record.unlocode === 'string' && record.unlocode.trim()) ||
        (typeof record.locationCode === 'string' && record.locationCode.trim()) ||
        '';

      return {
        id: record.id,
        slug: record.slug || '',
        name: record.name || '',
        displayName:
          record.displayName ||
          record.legalName ||
          record.tradeName ||
          record.name ||
          '',
        companyName: record.name || '',
        description: record.description || record.about || record.slogan || '',
        companyType:
          (typeof record.companyType === 'string' && record.companyType) ||
          (typeof record.company_type === 'string' && record.company_type) ||
          (typeof record.category === 'string' && record.category) ||
          '',
        location: buildCompanyLocation(record),
        heroUrl: getAssetUrl(record, 'hero'),
        logoUrl: getAssetUrl(record, 'logo'),
        isVerified: Boolean(record.isVerified),
        updatedAt: record.updated || record.updatedAt || record.updated_at || '',
        createdAt: record.created || record.createdAt || record.created_at || '',
        membershipPlan,
        rating,
        responseTime: responseTimeValue,
        coverage: coverageValue,
        tagline: taglineValue,
        city: record.city || '',
        locationUnlocode: locationUnlocode.toUpperCase(),
      };
    });
  } catch (error) {
    console.error('No se pudieron cargar las empresas publicadas.', error);
    if ([401, 403, 404].includes(error?.status)) {
      return [];
    }
    throw new Error(error?.message || 'No se pudieron cargar las empresas en este momento.');
  }
}

export function mapCompanyToPublication(companyRecord, publication = {}) {
  if (!companyRecord) {
    return {};
  }

  return {
    companyName: companyRecord.name || publication.companyName || '',
    companySlug: companyRecord.slug || '',
    companyLogoUrl: getAssetUrl(companyRecord, 'logo'),
    companyHeroUrl: getAssetUrl(companyRecord, 'hero'),
  };
}
